import React, { memo } from 'react';
import { Eye, Archive, ArchiveRestore, Ban, History as HistoryIcon, Flag } from 'lucide-react';
import { cn } from '../../infrastructure/lib/utils';
import type { AdminInvitation } from '../../infrastructure/lib/api';

const STATUS_TONE: Record<string, string> = {
  DRAFT: 'bg-gray-500/15 text-gray-300',
  PROCESSING: 'bg-blue-500/15 text-blue-300',
  PUBLISHED: 'bg-emerald-500/15 text-emerald-300',
  FAILED: 'bg-red-500/15 text-red-300',
  EXPIRED: 'bg-zinc-500/15 text-zinc-300',
  ARCHIVED: 'bg-amber-500/15 text-amber-300',
  CANCELLED: 'bg-rose-500/15 text-rose-300',
  SCHEDULED: 'bg-sky-500/15 text-sky-300',
  SUSPENDED: 'bg-red-500/20 text-red-300',
  REPORTED: 'bg-orange-500/15 text-orange-300',
};

interface InvitationRowProps {
  inv: AdminInvitation;
  isChecked: boolean;
  busy: boolean;
  toggleSelect: (id: string) => void;
  openHistory: (inv: AdminInvitation) => void;
  onRowAction: (id: string, action: 'archive' | 'suspend' | 'restore') => void;
  formatDateCell: (iso?: string | null) => string;
}

export const InvitationRow = memo(function InvitationRow({
  inv,
  isChecked,
  busy,
  toggleSelect,
  openHistory,
  onRowAction,
  formatDateCell,
}: InvitationRowProps) {
  return (
    <tr
      className={cn(
        'border-t border-border-subtle hover:bg-white/[0.02]',
        isChecked && 'bg-electric-violet/5',
      )}
    >
      <td className="px-3 py-3">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => toggleSelect(inv.id)}
        />
      </td>
      <td className="px-3 py-3">
        <div className="font-bold text-on-surface line-clamp-1">{inv.eventName}</div>
        <div className="text-[10px] uppercase tracking-tight text-on-surface-variant">
          {inv.occasionType}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="font-medium text-on-surface line-clamp-1">{inv.user.name}</div>
        <div className="text-[11px] text-on-surface-variant line-clamp-1">
          {inv.user.email}
        </div>
      </td>
      <td className="px-3 py-3">
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight',
            STATUS_TONE[inv.status] ?? 'bg-white/10 text-on-surface-variant',
          )}
        >
          {inv.status}
        </span>
      </td>
      <td className="px-3 py-3 text-on-surface-variant">{formatDateCell(inv.eventDate)}</td>
      <td className="px-3 py-3 text-on-surface-variant">{formatDateCell(inv.expiresAt)}</td>
      <td className="px-3 py-3 text-right tabular-nums">{inv.totalViews ?? 0}</td>
      <td className="px-3 py-3 text-right tabular-nums">{inv.totalRsvps ?? 0}</td>
      <td className="px-3 py-3 text-right tabular-nums">
        {inv.reportedCount && inv.reportedCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-orange-300">
            <Flag className="w-3 h-3" />
            {inv.reportedCount}
          </span>
        ) : (
          0
        )}
      </td>
      <td className="px-3 py-3 text-right text-on-surface-variant">
        {formatDateCell(inv.updatedAt)}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center justify-end gap-1">
          {inv.hostedUrl && (
            <a
              href={inv.hostedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-white/5 text-on-surface-variant"
              title="Preview"
              aria-label="Preview"
            >
              <Eye className="w-4 h-4" />
            </a>
          )}
          <button
            type="button"
            onClick={() => void openHistory(inv)}
            className="p-1.5 rounded hover:bg-white/5 text-on-surface-variant"
            title="History"
            aria-label="History"
          >
            <HistoryIcon className="w-4 h-4" />
          </button>
          {inv.status === 'SUSPENDED' || inv.status === 'ARCHIVED' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onRowAction(inv.id, 'restore')}
              className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-300"
              title="Restore"
              aria-label="Restore"
            >
              <ArchiveRestore className="w-4 h-4" />
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRowAction(inv.id, 'archive')}
                className="p-1.5 rounded hover:bg-amber-500/10 text-amber-300"
                title="Archive"
                aria-label="Archive"
              >
                <Archive className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRowAction(inv.id, 'suspend')}
                className="p-1.5 rounded hover:bg-red-500/10 text-red-300"
                title="Suspend"
                aria-label="Suspend"
              >
                <Ban className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
});
